import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  addAdditionalColleagues,
  getTeacherAccountLookup,
  resendTeacherSetupLink
} from "../services/APIClient";
import { isPlaceholderWorkflowId } from "../utils/teacherWorkflowRequests";
import "./TeacherActivateTeacherAccountModal.css";

const normalise = (value) => String(value || "").trim();
const normaliseKey = (value) => normalise(value).toLowerCase();

const getFirstName = (name) => normalise(name).split(/\s+/)[0] || "The teacher";

const splitTeacherName = (teacherName) => {
  const nameParts = normalise(teacherName).split(/\s+/).filter(Boolean);
  return {
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ")
  };
};

const getTeacherId = (teacher) =>
  teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id;

const getTeacherName = (teacher) =>
  normalise(
    teacher?.name ||
    teacher?.fullName ||
    teacher?.displayName ||
    [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ")
  ) || "Unnamed teacher";

const normaliseTeacher = (teacher, fallbackStatus, canSetup) => ({
  ...teacher,
  id: teacher?.pendingteacherid || teacher?.pendingTeacherId || getTeacherId(teacher),
  pendingteacherid: teacher?.pendingteacherid || teacher?.pendingTeacherId || teacher?.pending_teacher_id || getTeacherId(teacher),
  name: getTeacherName(teacher),
  email: normalise(teacher?.email),
  status: teacher?.status || teacher?.accountStatus || teacher?.account_status || fallbackStatus,
  availableActions: teacher?.availableActions || teacher?.available_actions || [],
  canSetup: canSetup || (teacher?.availableActions || teacher?.available_actions || []).includes("resend_setup_link")
});

const findSingleExactTeacher = (teachers, prefill) => {
  const name = normaliseKey(prefill?.teacherName);
  const email = normaliseKey(prefill?.email);

  if (!name && !email) {
    return null;
  }

  const matches = teachers.filter((teacher) =>
    (email && normaliseKey(teacher.email) === email) ||
    (name && normaliseKey(teacher.name) === name)
  );

  return matches.length === 1 ? matches[0] : null;
};

const getLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.teachers)) return lookupResponse.teachers;
  if (Array.isArray(lookupResponse?.rows)) return lookupResponse.rows;
  if (Array.isArray(lookupResponse?.accounts)) return lookupResponse.accounts;
  return [];
};

function TeacherActivateTeacherAccountModal({
  show,
  handleClose,
  schoolid,
  rollnumber = "",
  rollNumber = "",
  currentTeacherName = "",
  countryId = null,
  prefill = null,
  onSendAccountSetup = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teacherSearch, setTeacherSearch] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [sentTeacher, setSentTeacher] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;

    setStage("form");
    setTeacherSearch(prefill?.teacherName || "");
    setInviteName(prefill?.teacherName || "");
    setInviteEmail(prefill?.email || "");
    setTeachers([]);
    setSelectedTeacher(null);
    setSentTeacher(null);
    setError("");

    if (!schoolid) return;
    getTeacherAccountLookup(schoolid).then((lookupResponse) => {
      const normalisedTeachers = getLookupRows(lookupResponse)
        .map((teacher) =>
          normaliseTeacher(
            teacher,
            teacher?.accountStatus || teacher?.account_status || "Unknown",
            (teacher?.availableActions || teacher?.available_actions || []).includes("resend_setup_link")
          )
        )
        .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));

      setTeachers(normalisedTeachers);
      setSelectedTeacher(findSingleExactTeacher(normalisedTeachers, prefill));
    });
  }, [show, schoolid, prefill]);

  const filteredTeachers = useMemo(() => {
    const query = teacherSearch.trim().toLowerCase();

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.status].some((value) =>
        normaliseKey(value).includes(query)
      )
    );
  }, [teachers, teacherSearch]);

  const sendSetupLink = async () => {
    if (!selectedTeacher || isSubmitting) return;
    if (!selectedTeacher.canSetup) {
      setError("This teacher already has an active account.");
      return;
    }

    if (!selectedTeacher.id) {
      setError("Choose a listed invited teacher before sending a setup link.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      const pendingTeacherId = selectedTeacher.pendingteacherid || selectedTeacher.id;

      if (isPlaceholderWorkflowId(pendingTeacherId)) {
        throw new Error("Missing real pending teacher id.");
      }

      await resendTeacherSetupLink(schoolid, pendingTeacherId);
      setSentTeacher(selectedTeacher);
      setStage("success");
    } catch (submitError) {
      console.error("Error sending setup link:", submitError);
      setError("The setup link could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendInvite = async () => {
    if (!normalise(inviteName) || !normalise(inviteEmail) || isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    const nameParts = splitTeacherName(inviteName);
    const invitedTeacher = {
      ...nameParts,
      name: inviteName,
      email: inviteEmail,
      roles: "Class teacher"
    };
    try {
      if (schoolid) {
        await addAdditionalColleagues(
          schoolid,
          rollnumber || rollNumber,
          [invitedTeacher],
          currentTeacherName,
          countryId
        );
      } else {
        throw new Error("Missing school context for teacher invite.");
      }
      setSentTeacher({ ...invitedTeacher, status: "Invited" });
      setStage("success");
    } catch (submitError) {
      console.error("Error inviting teacher:", submitError);
      setError("The invite could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTeacherSearchChange = (event) => {
    const nextSearch = event.target.value;
    setTeacherSearch(nextSearch);
    setError("");

    if (
      selectedTeacher &&
      normaliseKey(nextSearch) !== normaliseKey(selectedTeacher.name)
    ) {
      setSelectedTeacher(null);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherSearch(teacher.name);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: sentTeacher?.email || sentTeacher?.name || "",
        message: "Teacher account activation complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherResults = () => {
    if (!teacherSearch.trim()) {
      return null;
    }

    if (selectedTeacher) {
      return (
        <button
          aria-pressed
          className="activate-teacher-row selected"
          key={`${selectedTeacher.id || selectedTeacher.name}-${selectedTeacher.email}`}
          type="button"
          onClick={() => handleTeacherSelect(selectedTeacher)}
        >
          <span className="activate-teacher-radio" aria-hidden="true" />
          <span>
            <strong>{selectedTeacher.name}</strong>
            <span>{selectedTeacher.email} - {selectedTeacher.status}</span>
          </span>
          <span className="activate-teacher-selected-label">Selected</span>
        </button>
      );
    }

    if (filteredTeachers.length < 1) {
      return <p className="activate-teacher-muted">No matching teachers found.</p>;
    }

    return filteredTeachers.slice(0, 5).map((teacher) => (
      <button
        aria-pressed={false}
        className="activate-teacher-row"
        key={`${teacher.id || teacher.name}-${teacher.email}`}
        type="button"
        onClick={() => handleTeacherSelect(teacher)}
      >
        <span className="activate-teacher-radio" aria-hidden="true" />
        <span>
          <strong>{teacher.name}</strong>
          <span>{teacher.email} - {teacher.status}</span>
        </span>
      </button>
    ));
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Activate Teacher Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="activate-teacher-intro">Find the teacher or invite them if they are not listed.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <h3 className="activate-teacher-question">Which teacher needs account activation?</h3>
        <Form.Group className="mb-3" controlId="activateTeacherSearch">
          <Form.Label>Search teacher</Form.Label>
          <Form.Control value={teacherSearch} onChange={handleTeacherSearchChange} />
          <Form.Text>Start typing a teacher name.</Form.Text>
        </Form.Group>
        <div className="activate-teacher-results">
          {renderTeacherResults()}
        </div>
        <h3 className="activate-teacher-question mt-4">Teacher not listed?</h3>
        <button className="activate-teacher-row" type="button" onClick={() => setStage("invite")}>
          <span className="activate-teacher-radio" aria-hidden="true" />
          <span>
            <strong>Invite them now</strong>
            <span>Add their name and email to send a setup invite.</span>
          </span>
        </button>
      </Modal.Body>
      <Modal.Footer>
        <span className="activate-teacher-footer-note">{selectedTeacher ? `${selectedTeacher.name} selected.` : "Choose a teacher."}</span>
        <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={sendSetupLink} disabled={!selectedTeacher || isSubmitting}>Send setup link</Button>
      </Modal.Footer>
    </>
  );

  const renderInvite = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Invite Teacher Now</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="activate-teacher-intro">Use this when the teacher is not in the search results.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="activate-teacher-warning">
          <strong>Teacher details</strong>
          <div>Enter the teacher name and email to send a setup invite.</div>
        </Alert>
        <h3 className="activate-teacher-question">Invite details</h3>
        <Form.Group className="mb-3" controlId="activateInviteName">
          <Form.Label>Teacher name</Form.Label>
          <Form.Control value={inviteName} onChange={(event) => setInviteName(event.target.value)} />
        </Form.Group>
        <Form.Group controlId="activateInviteEmail">
          <Form.Label>Teacher email</Form.Label>
          <Form.Control type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="text-muted me-auto">Invite details entered.</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
        <Button variant="primary" onClick={sendInvite} disabled={!normalise(inviteName) || !normalise(inviteEmail) || isSubmitting}>
          Send invite
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Setup Link Sent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="activate-teacher-intro">{getFirstName(sentTeacher?.name)} can use the email link to activate her account.</p>
        <Alert variant="success" className="activate-teacher-success">
          <span className="activate-teacher-ok-badge">OK</span>
          <span>
            <strong>{sentTeacher?.name || "The teacher"} has been sent a setup link.</strong>
            <span>{getFirstName(sentTeacher?.name)} can use the email link to activate her account.</span>
          </span>
        </Alert>
        <div className="activate-teacher-next">
          <strong>What next?</strong>
          <div className="text-muted">
            {embedded
              ? "Done returns to SENSAI. Activate another account repeats the action."
              : "Done returns to Help Centre home. Activate another account repeats the action."}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="activate-teacher-footer-note">Task complete.</span>
        <Button
          variant="outline-secondary"
          onClick={() => {
            setStage("form");
            setTeacherSearch("");
            setInviteName("");
            setInviteEmail("");
            setSelectedTeacher(null);
            setSentTeacher(null);
            setError("");
          }}
        >
          Activate another account
        </Button>
        {onSendAccountSetup && (
          <Button
            variant="outline-secondary"
            onClick={() => onSendAccountSetup({
              teacherName: sentTeacher?.name
            })}
          >
            Send account setup link
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-activate-teacher-modal">
      {stage === "form" && renderForm()}
      {stage === "invite" && renderInvite()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherActivateTeacherAccountModal;
